import { useCallback, useEffect, useState } from "react";
import { ArrowClockwise, CheckCircle, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const MODE_META = {
  auto: ["Auto-send", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
  approval: ["Needs approval", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
  off: ["Off", "border-stone-300 bg-stone-100 text-stone-500"],
};
const STATUS_META = {
  pending_approval: ["Pending approval", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
  sent: ["Sent", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
  failed: ["Failed", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
  cancelled: ["Cancelled", "border-stone-300 bg-stone-100 text-stone-500"],
};
const Badge = ({ meta, value, testid }) => {
  const [label, cls] = meta[value] || ["—", "border-stone-300 bg-stone-100 text-stone-500"];
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={testid}>{label}</span>;
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("queue");
  const [rules, setRules] = useState([]);
  const [queue, setQueue] = useState([]);
  const [queueFilter, setQueueFilter] = useState("pending_approval");
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState({ subject: "", body: "" });
  const [ruleDrafts, setRuleDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, q] = await Promise.all([
        api.get("/notifications/rules"),
        api.get(`/notifications/queue${queueFilter ? `?status=${queueFilter}` : ""}`),
      ]);
      setRules(r.data.rules || []);
      setQueue(q.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [queueFilter]);
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role, load]);

  const saveRule = async (key) => {
    const d = ruleDrafts[key] || {};
    setBusy(`rule-${key}`);
    try {
      await api.put(`/notifications/rules/${key}`, d);
      toast.success("Rule saved");
      setRuleDrafts((s) => ({ ...s, [key]: {} }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const runReminders = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/notifications/training-reminders/run", {}, { timeout: 120000 });
      toast.success(`Reminders: ${data.sent} sent, ${data.queued} queued for approval, ${data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const approve = async (nid) => {
    setBusy(`approve-${nid}`);
    try {
      const { data } = await api.post(`/notifications/queue/${nid}/approve`);
      if (data.status === "sent") toast.success("Email sent");
      else toast.error("Send failed — check the queue entry");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const cancel = async (nid) => {
    try {
      await api.post(`/notifications/queue/${nid}/cancel`);
      toast.success("Notification cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveEdit = async (nid) => {
    try {
      await api.put(`/notifications/queue/${nid}`, editDraft);
      toast.success("Draft updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (user?.role !== "admin") {
    return <div className="rounded-lg border border-stone-200 bg-white p-8 text-sm text-stone-500">Notifications are admin-only.</div>;
  }

  return (
    <div className="max-w-6xl" data-testid="notifications-page">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Notifications</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-stone-500">
            Automated staff emails with per-rule control: auto-send, require your approval, or off. Anything needing approval lands in the queue — you can edit the wording before it goes out.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-stone-300" data-testid="notifications-refresh"><ArrowClockwise size={15} className="mr-1.5" /> Refresh</Button>
          <Button onClick={runReminders} disabled={busy === "run"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="run-training-reminders">
            <PaperPlaneTilt size={15} className="mr-1.5" /> {busy === "run" ? "Running…" : "Run training reminders"}
          </Button>
        </div>
      </div>

      <div className="mb-6 flex gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm" data-testid="notifications-tabs">
        {[["queue", "Approval queue"], ["rules", "Rules & templates"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`rounded-md px-4 py-2 text-sm font-medium ${tab === key ? "bg-primary text-primary-foreground" : "text-stone-600 hover:bg-accent"}`}
            data-testid={`notifications-tab-${key}`}>{label}</button>
        ))}
      </div>

      {tab === "rules" && (
        <div className="space-y-4" data-testid="rules-panel">
          {rules.map((r) => {
            const draft = ruleDrafts[r.key] || {};
            const dirty = Object.keys(draft).length > 0;
            return (
              <div key={r.key} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`rule-${r.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-stone-900">{r.label}</p>
                    <p className="mt-0.5 text-xs text-stone-500">{r.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge meta={MODE_META} value={draft.mode || r.mode} testid={`rule-mode-badge-${r.key}`} />
                    <select value={draft.mode || r.mode} onChange={(e) => setRuleDrafts((s) => ({ ...s, [r.key]: { ...draft, mode: e.target.value } }))}
                      className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid={`rule-mode-${r.key}`}>
                      <option value="auto">Auto-send</option>
                      <option value="approval">Needs approval</option>
                      <option value="off">Off</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 grid gap-3">
                  <div>
                    <Label className="text-xs">Subject template</Label>
                    <Input value={draft.subject ?? r.subject} onChange={(e) => setRuleDrafts((s) => ({ ...s, [r.key]: { ...draft, subject: e.target.value } }))} data-testid={`rule-subject-${r.key}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Body template — placeholders: {"{name}"}, {"{items}"}, {"{details}"}, {"{subject}"}</Label>
                    <Textarea rows={4} value={draft.body ?? r.body} onChange={(e) => setRuleDrafts((s) => ({ ...s, [r.key]: { ...draft, body: e.target.value } }))} data-testid={`rule-body-${r.key}`} />
                  </div>
                  {dirty && (
                    <Button onClick={() => saveRule(r.key)} disabled={busy === `rule-${r.key}`} className="w-fit bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`rule-save-${r.key}`}>
                      {busy === `rule-${r.key}` ? "Saving…" : "Save rule"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "queue" && (
        <div data-testid="queue-panel">
          <div className="mb-4 flex gap-1" data-testid="queue-filters">
            {[["pending_approval", "Pending approval"], ["sent", "Sent"], ["failed", "Failed"], ["cancelled", "Cancelled"], ["", "All"]].map(([key, label]) => (
              <button key={key} onClick={() => setQueueFilter(key)}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium ${queueFilter === key ? "border-primary bg-accent text-accent-foreground" : "border-stone-300 text-stone-600 hover:bg-accent"}`}
                data-testid={`queue-filter-${key || "all"}`}>{label}</button>
            ))}
          </div>
          <div className="space-y-3">
            {queue.length === 0 && <p className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-400" data-testid="queue-empty">Nothing here.</p>}
            {queue.map((n) => (
              <div key={n.id} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`queue-item-${n.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{n.recipient_name || n.recipient_email}</p>
                    <p className="text-xs text-stone-400">{n.recipient_email} · {n.rule_key.replace(/_/g, " ")} · {String(n.created_at).slice(0, 16).replace("T", " ")}</p>
                  </div>
                  <Badge meta={STATUS_META} value={n.status} testid={`queue-status-${n.id}`} />
                </div>
                {editing === n.id ? (
                  <div className="mt-3 space-y-2">
                    <Input value={editDraft.subject} onChange={(e) => setEditDraft({ ...editDraft, subject: e.target.value })} data-testid={`queue-edit-subject-${n.id}`} />
                    <Textarea rows={6} value={editDraft.body} onChange={(e) => setEditDraft({ ...editDraft, body: e.target.value })} data-testid={`queue-edit-body-${n.id}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(n.id)} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`queue-edit-save-${n.id}`}>Save draft</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="border-stone-300" data-testid={`queue-edit-cancel-${n.id}`}>Close</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 rounded-md bg-stone-50 p-3" data-testid={`queue-body-${n.id}`}>
                    <p className="text-xs font-semibold text-stone-600">{n.subject}</p>
                    <p className="mt-1 whitespace-pre-wrap text-xs text-stone-600">{n.body}</p>
                    {n.error && <p className="mt-1 text-xs text-[#C0392B]">Error: {n.error}</p>}
                  </div>
                )}
                {n.status === "pending_approval" && editing !== n.id && (
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={() => approve(n.id)} disabled={busy === `approve-${n.id}`} className="bg-[#1e8449] text-white hover:bg-[#156b3a]" data-testid={`queue-approve-${n.id}`}>
                      <CheckCircle size={14} className="mr-1" /> {busy === `approve-${n.id}` ? "Sending…" : "Approve & send"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditing(n.id); setEditDraft({ subject: n.subject, body: n.body }); }} className="border-stone-300" data-testid={`queue-edit-${n.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => cancel(n.id)} className="border-stone-300 text-stone-500" data-testid={`queue-cancel-${n.id}`}><X size={14} className="mr-1" /> Cancel</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
